import { InstagramUserImage, TiktokUserImage, getInstagramUserImages, getTiktokUserImages } from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import { showNotification } from '../utils/showNotification';
import useAbortableEffect from './useAbortableEffect';

const fetchTiktokUserImages = async (ids: number[], requestInit?: RequestInit) => {
    if (!ids.length) {
        return [];
    }

    return getTiktokUserImages(ids, requestInit);
};

const fetchInstagramImages = async (ids: number[], requestInit?: RequestInit) => {
    if (!ids.length) {
        return [];
    }

    return (await getInstagramUserImages(ids, requestInit)).data;
};

export function useInfluencerImages(tiktokUserIds: number[], instagramUserIds: number[]) {
    const [isLoading, setIsLoading] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);

    const [tiktokUserImages, setTiktokUserImages] = useState<TiktokUserImage[]>([]);
    const [instagramUserImages, setInstagramUserImages] = useState<InstagramUserImage[]>([]);

    const fetchInfluencerData = useCallback(
        async (tiktokUserIds: number[], instagramUserIds: number[], requestInit?: RequestInit) => {
            if (!tiktokUserIds.length && !instagramUserIds.length) {
                return;
            }

            try {
                setHasErrorLoading(false);
                setIsLoading(true);

                const [tiktokImagesResult, instagramImagesResult] = await Promise.allSettled([
                    fetchTiktokUserImages(tiktokUserIds, requestInit),
                    fetchInstagramImages(instagramUserIds, requestInit),
                ]);

                const tiktokImages: TiktokUserImage[] =
                    tiktokImagesResult.status === 'fulfilled' ? tiktokImagesResult.value : [];
                const instagramImages: InstagramUserImage[] =
                    instagramImagesResult.status === 'fulfilled' ? instagramImagesResult.value : [];

                setTiktokUserImages((prev) => prev.concat(tiktokImages));
                setInstagramUserImages((prev) => prev.concat(instagramImages));
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                showNotification((e as Error).message, 'error');
                setHasErrorLoading(true);
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    const activate = useCallback(() => setIsActive(true), []);

    const reset = useCallback(() => {
        setIsActive(false);
        setHasErrorLoading(false);
        setIsLoading(false);
        setTiktokUserImages([]);
        setInstagramUserImages([]);
    }, []);

    const filteredTiktokUserIds = useMemo(
        () => tiktokUserIds.filter((id) => !tiktokUserImages.some((image) => image.user_id === id)),
        [tiktokUserIds, tiktokUserImages]
    );
    const filteredInstagramUserIds = useMemo(
        () => instagramUserIds.filter((id) => !instagramUserImages.some((image) => image.user_id === id)),
        [instagramUserIds, instagramUserImages]
    );

    useAbortableEffect(
        (signal) => {
            if (!isActive) {
                return;
            }
            fetchInfluencerData(filteredTiktokUserIds, filteredInstagramUserIds, { signal });
        },
        [isActive, fetchInfluencerData, filteredTiktokUserIds, filteredInstagramUserIds]
    );

    return {
        tiktokUserImages,
        instagramUserImages,
        hasErrorLoading,
        isLoading,
        isActive,
        activate,
        reset,
    };
}
